// Meta (Facebook) Pixel helpers.
//
// The base snippet in client/index.html defines window.fbq, initializes the
// "VACATION RENTAL OAHU" pixel, and fires the first PageView. These helpers cover what the
// snippet cannot: PageView on SPA route changes (wouter never reloads the document) and the
// conversion events that let Meta optimize delivery and attribute revenue.
//
// Every call is a no-op when fbq is unavailable (SSR, ad blockers), so callers never need
// to guard.

export const META_PIXEL_ID = "879328698568735";

type FbqFn = (...args: any[]) => void;

function getFbq(): FbqFn | null {
  if (typeof window === "undefined") return null;
  const f = (window as any).fbq;
  return typeof f === "function" ? f : null;
}

export function trackPageView(): void {
  getFbq()?.("track", "PageView");
}

/**
 * Fire a Meta standard event (PageView, ViewContent, Lead, InitiateCheckout, Purchase...).
 *
 * `eventId` is Meta's deduplication key. The same id sent from the browser and from the
 * server (Conversions API) counts once, and a browser event that fires twice for one user
 * action (a re-rendered handler, a retried request) also counts once.
 */
export function trackEvent(event: string, params?: Record<string, any>, eventId?: string): void {
  const fbq = getFbq();
  if (!fbq) return;
  if (eventId) fbq("track", event, params, { eventID: eventId });
  else fbq("track", event, params);
}

/** A unique event id for one user action, e.g. `ic_1789...-k3j9x`. */
export function newEventId(prefix: string): string {
  const rand =
    typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
      ? crypto.randomUUID()
      : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
  return `${prefix}_${rand}`;
}

/**
 * Fire an event at most once per key, persisted across reloads. Used for Purchase so a
 * guest refreshing the confirmation page cannot inflate conversions or revenue.
 *
 * The key doubles as the Meta event id, so the server-side Purchase sent from the Stripe
 * webhook (server/meta-capi.ts) with the same `purchase_<bookingId>` id deduplicates
 * against this one instead of double counting the sale.
 */
export function trackOnce(key: string, event: string, params?: Record<string, any>): void {
  const storageKey = `fbq_once_${key}`;
  try {
    if (localStorage.getItem(storageKey)) return;
    localStorage.setItem(storageKey, "1");
  } catch {
    // localStorage unavailable: fall through and still track (better than losing the event)
  }
  trackEvent(event, params, key);
}
